import { Button, Flex, Loader, Modal, Progress, Stack, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	BulkRemindSignersJobStatus,
	type BulkRemindSignersJobStatusRes,
} from '@dealstack/shared/lib/backendClient';
import {
	BULK_REMIND_SIGNERS_TERMINAL_STATUSES,
	type ResultBucketRowProps,
} from './BulkRemindSignersModal.types';

interface Props {
	jobStatus: BulkRemindSignersJobStatusRes | null;
	onClose: () => void;
}

// Renders a single result bucket (label + raw count) as its own DOM node, keyed by testId, so
// every bucket is independently assertable and none can be silently dropped by an interpolated
// summary string.
const ResultBucketRow = ({ testId, labelKey, count, tMipManage }: ResultBucketRowProps) => (
	<Text size="xs" c="dimmed" data-testid={testId}>
		{tMipManage(labelKey)}
		{': '}
		{count}
	</Text>
);

export const BulkRemindSignersModal = ({ jobStatus, onClose }: Props) => {
	const tMipManage = useTranslationMipManage();

	const isTerminal =
		!!jobStatus && BULK_REMIND_SIGNERS_TERMINAL_STATUSES.includes(jobStatus.status);

	const subtitleKey = (() => {
		if (jobStatus?.status === BulkRemindSignersJobStatus.FAILED) {
			return 'documents_page.bulk_remind_signers_modal.progress_failed';
		}
		if (jobStatus?.status === BulkRemindSignersJobStatus.COMPLETED) {
			return 'documents_page.bulk_remind_signers_modal.progress_completed';
		}
		return 'documents_page.bulk_remind_signers_modal.progress_in_progress';
	})();

	const renderContent = () => {
		if (!jobStatus) {
			return (
				<Flex justify="center" align="center" p="xl">
					<Loader />
				</Flex>
			);
		}

		const percent =
			jobStatus.total > 0
				? Math.min(100, Math.round((jobStatus.processed / jobStatus.total) * 100))
				: 0;

		return (
			<Flex direction="column" gap="md" p="md">
				<Text size="sm">
					{tMipManage(subtitleKey, {
						processed: jobStatus.processed,
						total: jobStatus.total,
					})}
				</Text>
				<Progress
					value={percent}
					size="lg"
					color={jobStatus.status === BulkRemindSignersJobStatus.FAILED ? 'red' : 'blue'}
				/>
				{isTerminal &&
					(() => {
						// Only surface buckets that actually have a count — an all-zero grid
						// (e.g. a document with no pending signers) is noise. Each non-zero
						// bucket is still its own DOM node so it stays independently assertable
						// and can't be silently dropped by an interpolated summary string.
						const buckets = [
							{
								testId: 'bulk-remind-signers-reminded-count',
								labelKey:
									'documents_page.bulk_remind_signers_modal.result_reminded',
								count: jobStatus.reminded,
							},
							{
								testId: 'bulk-remind-signers-skipped-cooldown-count',
								labelKey:
									'documents_page.bulk_remind_signers_modal.result_skipped_cooldown',
								count: jobStatus.skippedCooldown,
							},
							{
								testId: 'bulk-remind-signers-skipped-no-email-count',
								labelKey:
									'documents_page.bulk_remind_signers_modal.result_skipped_no_email',
								count: jobStatus.skippedNoEmail,
							},
							{
								testId: 'bulk-remind-signers-skipped-no-user-count',
								labelKey:
									'documents_page.bulk_remind_signers_modal.result_skipped_no_user',
								count: jobStatus.skippedNoUser,
							},
							{
								testId: 'bulk-remind-signers-skipped-not-remindable-count',
								labelKey:
									'documents_page.bulk_remind_signers_modal.result_skipped_not_remindable',
								count: jobStatus.skippedNotRemindable,
							},
							{
								testId: 'bulk-remind-signers-failed-count',
								labelKey: 'documents_page.bulk_remind_signers_modal.result_failed',
								count: jobStatus.failed,
							},
						].filter((bucket) => bucket.count > 0);

						if (buckets.length === 0) {
							return (
								<Text
									size="sm"
									c="dimmed"
									data-testid="bulk-remind-signers-no-results"
								>
									{tMipManage(
										'documents_page.bulk_remind_signers_modal.no_signers'
									)}
								</Text>
							);
						}

						return (
							<Stack gap={4} data-testid="bulk-remind-signers-result-breakdown">
								{buckets.map((bucket) => (
									<ResultBucketRow
										key={bucket.testId}
										testId={bucket.testId}
										labelKey={bucket.labelKey}
										count={bucket.count}
										tMipManage={tMipManage}
									/>
								))}
							</Stack>
						);
					})()}
				{jobStatus.errors.length > 0 && (
					<Stack gap={4}>
						{jobStatus.errors.map((err, idx) => (
							<Text key={idx} size="xs" c="red">
								{err}
							</Text>
						))}
					</Stack>
				)}
				<Flex justify="flex-end">
					<Button variant="filled" onClick={onClose} disabled={!isTerminal}>
						{tMipManage('documents_page.bulk_remind_signers_modal.close_button')}
					</Button>
				</Flex>
			</Flex>
		);
	};

	return (
		<Modal
			opened={true}
			onClose={onClose}
			title={tMipManage('documents_page.bulk_remind_signers_modal.title')}
			centered
			size="lg"
		>
			{renderContent()}
		</Modal>
	);
};
